import React, {useEffect, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {useService} from '../../../infrastructure/service/service';
import {State} from '../../../entity/model/state';
import {County} from '../../../entity/model/county';
import {TitleTemplate} from '../../../entity/model/title-template';
import {SearchDto} from '../../../entity/dto/search-dto';
import {SearchEntity} from '../../../entity/enums/search-entity';
import {User} from '../../../entity/model/user';
import {SearchResponse} from '../../../entity/resource/search-response';
import {ManageMode} from '../../../entity/enums/manage-mode';
import {UserPermission} from '../../../entity/enums/user-permission';
import {TitleTemplateRequest} from '../../../entity/resource/title-template-request';
import {TemplateType} from '../../../entity/enums/template-type';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Select, SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import TableSortLabel from '@mui/material/TableSortLabel';
import LoadingComponent from '../components/widget/LoadingComponent';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import CopyAllIcon from '@mui/icons-material/CopyAll';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import Skeleton from 'react-loading-skeleton';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ConfirmDialog from '../components/widget/ConfirmDialog';
import TemplateDialog from '../components/title-template/TemplateDialog';
import {useLocation} from 'react-router-dom';
import {PageOption} from '../../../entity/enums/page-option';
import TablePagination from '../components/widget/TablePagination';
import {Form, Formik, FormikProps} from 'formik';
import AdvancedSearchDialog from '../components/title-template/AdvancedSearchDialog';
import {AdvancedSearchDto} from '../../../entity/dto/advanced-search-dto';
import {router} from '../../router/Routes';
import EditNoteIcon from '@mui/icons-material/EditNote';
import {SearchOptionDto} from '../../../entity/dto/search-option-dto';
import {TitleSearchDto} from '../../../entity/dto/title-search-dto';
import {SortType} from '../../../entity/enums/sort-type';
import {getTemplateColumnHeader, TemplateColumn} from '../../../entity/enums/template-column';
import {Title} from '../../../entity/model/title';
import {Project} from '../../../entity/model/project';

// @ts-ignore
import logo from '../../../assets/images/logo/logo.png'
import '../../../assets/css/style.css';
import '../../../assets/css/pages/title-bank.css';

const TitleBank = (): JSX.Element => {
  const {stateService, countyService, searchService, userService, titleTemplateService, titleService, projectService} = useService();
  const location = useLocation();

  const [searchInput, setSearchInput] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [trigger, setTrigger] = useState<boolean>(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
  const [loadingConfirmBtn, setLoadingConfirmBtn] = useState<boolean>(false);
  const [titleTemplates, setTitleTemplates] = useState<TitleTemplate[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [counties, setCounties] = useState<County[]>([]);
  const [currentUser, setCurrentUser] = useState<User>();
  const [totalPages, setTotalPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [openAdvancedSearch, setOpenAdvancedSearch] = useState<boolean>(false);
  const [advanceSearchDto, setAdvancedSearchDto] = useState<AdvancedSearchDto>();
  const [openTemplateDialog, setOpenTemplateDialog] = useState<boolean>(false);
  const [mode, setMode] = useState<ManageMode>(ManageMode.VIEW);
  const [selectedTemplate, setSelectedTemplate] = useState<TitleTemplate>();
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [createPermission, setCreatePermission] = useState<boolean>(false);
  const [updatePermission, setUpdatePermission] = useState<boolean>(false);
  const [importPermission, setimportPermission] = useState<boolean>(false);
  const [isPublic, setPublic] = useState(false);
  const [selected, setSelected] = React.useState<readonly string[]>([]);
  const [menuElement, setMenuElement] = useState<null | HTMLElement>(null);
  const [importTitle, setImportTitle] = useState<Title>();
  const [sortMap, setSortMap] = useState<Map<TemplateColumn, SortType>>(new Map());
  const [orderNumber, setOrderNumber] = useState<string>('');

  const pageSize = 100;
  const tableHeaders = [
    'Order Name',
    'Instrument Number (Book and Page)',
    'TMS/Parcel Numbers',
    'Address',
    'Subdivision',
    'County',
    'State'
  ];

  useEffect(() => {
    const init = async (): Promise<void> => {
      if (titleService.getPageOption() === PageOption.IMPORT) {
        let title: Title = JSON.parse(location.state);
        if (title !==  null) {
          setImportTitle(title);

          const project = await projectService.getProjectById(title.createdByOrganizationID, title.projectID);
          setOrderNumber(project?.orderNumber ?? '');
        }
      }

      setCurrentPage(1);

      const user = await userService.getCurrentUser();
      setCurrentUser(user);

      const states = await stateService.getStates();
      setStates(states);

      const counties = await countyService.getCounties();
      setCounties(counties);

      setCreatePermission(userService.isPermitted(UserPermission.CREATE_ORG_TITLE_TEMPLATE));
      setUpdatePermission(userService.isPermitted(UserPermission.UPDATE_ORG_TITLE_TEMPLATE));

      let projects = await projectService.getProjects(user?.organizationID ?? '', userService.isAdminUser());
      projects = projects.filter((project: Project): boolean => projectService.isPermittedProject(project.id));
      setimportPermission(projects.length > 0);

      const cache = titleTemplateService.getTitleBankCache();
      if (cache && cache.enabled) {
        setTrigger(true);
        setSearchInput(cache.searchInput);
        setPublic(cache.isPublic);
        setTitleTemplates(cache.templates);

        titleTemplateService.clearTitleBankCache();
      }
    }

    init();
  }, []);

  const handleDownloadAll = async (): Promise<void> => {
    setSubmitLoading(true);

    const titleTemplateRequests = [];
    for (const titleTemplate of titleTemplates.filter(titleTemplate => selected.includes(titleTemplate.id))) {
      const titleTemplateRequest: TitleTemplateRequest = {
        sections: titleTemplate.sections,
        templateName: titleTemplate.name.replace(/[\/\\:*?"<>|]/g, '_'),
        tmsNumbers: titleTemplate.tmsParcelNumbers.join(', '),
        state: states.find((state: State) => state.id === titleTemplate.stateID)?.shortName ?? '',
        county: counties.find((county: County) => county.id === titleTemplate.countyID)?.name ?? '',
        address: titleTemplate.address,
        subdivision: titleTemplate.subdivision,
        templateType: titleTemplate.isPublic ? TemplateType.PUBLIC : TemplateType.ORGANIZATION
      }

      titleTemplateRequests.push(titleTemplateRequest);
    }

    await titleService.downloadTitle(new Map(), titleTemplateRequests, states, counties);

    setSelected([]);
    setSubmitLoading(false);
  }

  const handleSearchChange = (handleChange: (event: React.ChangeEvent<any>) => void) => (event: React.ChangeEvent<any>): void => {
    setSearchInput(event.target.value);
    handleChange(event);
  }

  const handleOrderTypeChange = (handleChange: (event: SelectChangeEvent<any>) => void) => (event: SelectChangeEvent<any>): void => {
    setPublic(event.target.value === TemplateType.PUBLIC);
    handleChange(event);
  };

  const handleSearch = (event: React.KeyboardEvent<HTMLDivElement>, query: string): void => {
    if (event.key === 'Enter') {
      setLoading(true);
      setTrigger(true);
      setOpenAdvancedSearch(false);

      searchTitleTemplates(currentPage, query).then(() => setLoading(false));
    }
  }

  const handleSearchTemplates = (page: number, values?: AdvancedSearchDto, sortColumn?: TemplateColumn, sortType?: SortType): void => {
    setLoading(true);
    setTrigger(true);
    setOpenAdvancedSearch(false);

    searchTitleTemplates(page, searchInput, values, sortColumn, sortType).then(() => setLoading(false));
  }

  const searchTitleTemplates = async (page: number, query: string, values?: AdvancedSearchDto, sortColumn?: TemplateColumn, sortType?: SortType): Promise<void> => {
    setTitleTemplates([]);
    const searchOptions = getSearchOptions(values);

    const searchDto: SearchDto = {
      query: query && query !== '' ? query : (searchOptions.length > 0 ? searchOptions[0].value : ''),
      page: page,
      pageSize: pageSize,
      field: ['name', 'address', 'countyID', 'stateID', 'tmsParcelNumbers', 'subdivision', 'sections.documents.name'],
      options: searchOptions,
      entity: SearchEntity.TITLE_TEMPLATE,
      searchPublicTitleTemplates: isPublic,
      sortField: sortColumn,
      sortOrder: sortType
    };
    const response = await searchService.searchEntity(currentUser?.organizationID ?? '', searchDto) as SearchResponse;

    if (response && response.titleTemplates) {
      const lastPageSize = response.totalCount % response.pageSize;
      const pageCount = Math.trunc(response.totalCount / response.pageSize);

      setTitleTemplates(response.titleTemplates);
      setTotalPages((lastPageSize === 0 ? pageCount : pageCount + 1));
    }
  }

  const getSearchOptions = (values?: AdvancedSearchDto): SearchOptionDto[] => {
    const searchOptions: SearchOptionDto[] = [];

    if (values && values.state !== '')
      searchOptions.push({field: 'stateID', value: values.state});

    if (values && values.county !== '')
      searchOptions.push({field: 'countyID', value: values.county});

    if (values && values.templateName !== '')
      searchOptions.push({field: 'name', value: values.templateName});

    if (values && values.tmsNumbers !== '')
      searchOptions.push({field: 'tmsParcelNumbers', value: values.tmsNumbers});

    if (values && values.bookAndPage !== '')
      searchOptions.push({field: 'sections.documents.name', value: values.bookAndPage});

    if (values && values.address !== '')
      searchOptions.push({field: 'address', value: values.address});

    if (values && values.subdivision !== '')
      searchOptions.push({field: 'subdivision', value: values.subdivision});

    return searchOptions;
  }

  const handleAdvanceFilter = (): void => {
    setTrigger(false);
    setOpenAdvancedSearch(!openAdvancedSearch);
  }

  const handleCreateTemplate = (): void => {
    setSelectedTemplate(undefined);
    setMode(ManageMode.CREATE);
    setOpenTemplateDialog(true);
  }

  const handleAdvanceSearch = (values: AdvancedSearchDto): void => {
    setCurrentPage(1);
    setAdvancedSearchDto(values);
    handleSearchTemplates(currentPage, values);
  }

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (event.target.checked) {
      const newSelected = titleTemplates.map((template) => template.id);
      setSelected(newSelected);
      return;
    }

    setSelected([]);
  }

  const handleSelect = (event: React.MouseEvent<unknown>, id: string): void => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: readonly string[] = [];

    if (selectedIndex === -1)
      newSelected = newSelected.concat(selected, id);
    else if (selectedIndex === 0)
      newSelected = newSelected.concat(selected.slice(1));
    else if (selectedIndex === selected.length - 1)
      newSelected = newSelected.concat(selected.slice(0, -1));
    else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, titleTemplate: TitleTemplate): void => {
    setMenuElement(event.currentTarget);
    setSelectedTemplate(titleTemplate);
  }

  const handleMenuClose = (): void => {
    setMenuElement(null);
  }

  const handlePaginationChange = (event: React.ChangeEvent<unknown>, activePage: number): void => {
    setCurrentPage(activePage);
    handleSearchTemplates(activePage);
  }

  const handleTemplateDialog = (mode: ManageMode): void => {
    handleMenuClose();
    setOpenTemplateDialog(true);
    setMode(mode);
  }

  const deleteAllPermission = (): boolean => {
    for (const titleTemplate of titleTemplates.filter((titleTemplate: TitleTemplate): boolean => selected.includes(titleTemplate.id)))
      if (currentUser && titleTemplate.createdByUserID !== currentUser.id)
        return false;

    return true;
  }

  const handleDelete = (): void => {
    handleMenuClose();
    setOpenConfirmDialog(true);
  }

  const handleEditTemplate = async (): Promise<void> => {
    handleMenuClose();

    if (selectedTemplate)
      titleTemplateService.setTitleTemplate(selectedTemplate);

    titleTemplateService.setTitleBankCache(searchInput, isPublic, titleTemplates);
    await router.navigate('/template-details');
  }

  const deleteTitleTemplates = async (): Promise<void> => {
    setLoadingConfirmBtn(true);

    if (selected.length > 1) {
      const selectedTemplates = titleTemplates.filter(titleTemplate => selected.includes(titleTemplate.id));

      for (let i = 0; i < selectedTemplates.length; i++)
        await titleTemplateService.deleteTitleTemplate(titleTemplates[i].createdByOrganizationID, titleTemplates[i].id, i === selectedTemplates.length - 1);

    } else
      await titleTemplateService.deleteTitleTemplate(selectedTemplate?.createdByOrganizationID ?? '', selectedTemplate?.id ?? '', true);

    setLoadingConfirmBtn(false);
    setOpenConfirmDialog(false);

    setSelected([]);
    setCurrentPage(1);
    handleSearchTemplates(1);
  }

  const handleSort = async (templateColumn?: TemplateColumn): Promise<void> => {
    if (templateColumn) {
      let sortDirection = SortType.ASC;
      const newMap = new Map();
      const sortItem = sortMap.get(templateColumn);

      if (sortItem)
        sortDirection = sortItem === SortType.ASC ? SortType.DESC : SortType.ASC;

      newMap.set(templateColumn, sortDirection);
      setSortMap(newMap);

      handleSearchTemplates(currentPage, undefined, templateColumn, sortDirection);
    }
  }

  const getSortDirection = (header: string): 'asc' | 'desc' => {
    const templateColumn = getTemplateColumnHeader(header);
    if (templateColumn) {
      const sortItem = sortMap.get(templateColumn);
      if (sortItem)
        return sortItem === SortType.ASC ? 'asc' : 'desc';
    }

    return 'desc';
  }

  const renderTableRow = (titleTemplate: TitleTemplate, index: number): JSX.Element => {
    const bookAndPage = titleTemplateService.findDocument(titleTemplate, searchInput, advanceSearchDto?.bookAndPage);

    return (
      <TableRow
        hover
        role='checkbox'
        tabIndex={-1}
        key={titleTemplate.id}
        selected={selected.includes(titleTemplate.id)}
        className={'table-cell'}>
        <TableCell className={'no-wrap'} padding='checkbox'>
          <IconButton
            id={`btn-title-bank-menu-${index}`}
            className={`zitles-menu-trigger ${selected.length > 1 ? 'disabled' : ''}`}
            color='inherit'
            disableRipple
            disableFocusRipple
            disabled={selected.length > 1}
            onClick={(event: React.MouseEvent<HTMLElement>): void => handleMenuOpen(event, titleTemplate)}>
            <MoreHorizIcon />
          </IconButton>
          {
            selectedTemplate?.id === titleTemplate.id && Boolean(menuElement) &&
              <Menu elevation={0} anchorEl={menuElement} open={Boolean(menuElement)} className={'zitles-menu'} onClose={handleMenuClose}>
                <MenuItem id={`menu-title-view-${index}`} disableRipple className={'zitles-menu-item'} onClick={(): void => handleTemplateDialog(ManageMode.VIEW)}>
                  <VisibilityIcon className={'zitles-icon'}/>
                  <Typography className={'zitles-menu-item button'}>View</Typography>
                </MenuItem>
                {
                  updatePermission && currentUser && titleTemplate.createdByOrganizationID === currentUser.organizationID &&
                    <>
                      <MenuItem id={`menu-title-quick-edit-${index}`} disableRipple className={'zitles-menu-item'} onClick={(): void => handleTemplateDialog(ManageMode.QUICK_UPDATE)}>
                        <EditNoteIcon className={'zitles-icon'}/>
                        <Typography className={'zitles-menu-item button'}>Quick Edit</Typography>
                      </MenuItem>
                      <MenuItem id={`menu-title-edit-${index}`} disableRipple className={'zitles-menu-item'} onClick={handleEditTemplate}>
                        <EditIcon className={'zitles-icon'} fontSize={'small'} sx={{marginLeft: '5px'}}/>
                        <Typography className={'zitles-menu-item button'}>Edit</Typography>
                      </MenuItem>
                    </>
                }
                {
                  importPermission &&
                    <MenuItem id={`menu-title-import-${index}`} disableRipple className={'zitles-menu-item'} onClick={(): void => handleTemplateDialog(ManageMode.IMPORT)}>
                      <ImportExportIcon className={'zitles-icon'}/>
                      <Typography className={'zitles-menu-item button'}>Import</Typography>
                    </MenuItem>
                }
                {
                  createPermission &&
                    <MenuItem id={`menu-title-clone-${index}`} disableRipple className={'zitles-menu-item'} onClick={(): void => handleTemplateDialog(ManageMode.CLONE)}>
                      <CopyAllIcon className={'zitles-icon'}/>
                      <Typography className={'zitles-menu-item button'}>Clone</Typography>
                    </MenuItem>
                }
                <MenuItem id={`menu-title-download-${index}`} disableRipple className={'zitles-menu-item'} onClick={(): void => handleTemplateDialog(ManageMode.DOWNLOAD)}>
                  <DownloadIcon className={'zitles-icon'}/>
                  <Typography className={'zitles-menu-item button'}>Download</Typography>
                </MenuItem>
                {
                  updatePermission && currentUser && titleTemplate.createdByOrganizationID === currentUser.organizationID && titleTemplate.createdByUserID === currentUser.id &&
                    <Box>
                      <Divider/>
                      <MenuItem id={`menu-title-delete-${index}`} disableRipple className={'zitles-menu-item'} onClick={handleDelete}>
                        <DeleteIcon className={'zitles-icon'}/>
                        <Typography className={'zitles-menu-item button'}>Delete</Typography>
                      </MenuItem>
                    </Box>
                }
              </Menu>
          }
        </TableCell>
        <TableCell className={'no-wrap table-checkbox body'} padding='checkbox'>
          <Checkbox id={`checkbox-title-${index}`} checked={selected.includes(titleTemplate.id)} onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => handleSelect(event, titleTemplate.id)}/>
        </TableCell>
        <TableCell id={`text-title-name-${index}`} className={'no-wrap table-cell-text'} align={'left'}>{ titleTemplate.name }</TableCell>
        <TableCell id={`text-title-book-page-${index}`} className={'no-wrap table-cell-text'} align={'left'}>
          {
            bookAndPage
              ? <Box className={'book-and-page-container'} onClick={(): Window | null => window.open(bookAndPage.url)}>
                  <PictureAsPdfIcon className={'zitles-icon'}/>
                  <Typography className={'book-and-page-header'}>{bookAndPage.name}</Typography>
                  <Typography className={'book-and-page-footer'}>inside the file</Typography>
                </Box>
              : ''
          }
        </TableCell>
        <TableCell id={`text-title-tms-numbers-${index}`} className={'no-wrap table-cell-text'} align={'left'}>{ titleTemplate.tmsParcelNumbers.join(', ') }</TableCell>
        <TableCell id={`text-title-address-${index}`} className={'no-wrap table-cell-text'} align={'left'}>{ titleTemplate.address }</TableCell>
        <TableCell id={`text-title-subdivision-${index}`} className={'no-wrap table-cell-text'} align={'left'}>{ titleTemplate.subdivision }</TableCell>
        <TableCell id={`text-title-county-${index}`} className={'no-wrap table-cell-text'} align={'left'}>
          {
            counties.length > 0
              ? titleTemplate.countyID === null ? '' : counties.find((county: County): boolean => county.id === titleTemplate.countyID)?.name ?? ''
              : <Skeleton/>
          }
        </TableCell>
        <TableCell id={`text-title-state-${index}`} className={'no-wrap table-cell-text'} align={'left'}>
          {
            states.length > 0
              ? titleTemplate.stateID === null ? '' : states.find((state: State): boolean => state.id === titleTemplate.stateID)?.shortName ?? ''
              : <Skeleton/>
          }
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      <Box className={'title-bank-toolbar'}>
        {
          createPermission &&
            <Button
              id={'btn-create-template'}
              className={'zitles-button'}
              variant='contained'
              startIcon={<AddIcon />}
              onClick={handleCreateTemplate}>
              Create Template
            </Button>
        }
      </Box>
      <Box className={'title-search-container'}>
        {
          !trigger && !openAdvancedSearch &&
            <img src={logo} alt={'Zitles Title bank'}/>
        }
        <Formik initialValues={titleTemplateService.getTitleSearchDto(isPublic, searchInput)} onSubmit={(): void => {}}>
          {({values, handleChange}: FormikProps<TitleSearchDto>): JSX.Element => (
            <Form>
              <Box className={`search-container ${(trigger || openAdvancedSearch) ? 'trigger' : ''}`}>
                <TextField
                  id={'search-bar-title-bank'}
                  name='searchInput'
                  value={values.searchInput}
                  className={'search-input'}
                  variant={'outlined'}
                  placeholder="What are you looking for?"
                  onChange={handleSearchChange(handleChange)}
                  onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>): void => handleSearch(event, values.searchInput)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon className={'zitles-searchbar icon'}/>
                      </InputAdornment>)
                  }}/>
                <Select
                  id={'select-title-type'}
                  name='orderType'
                  className={'order-type-input'}
                  value={values.orderType}
                  onChange={handleOrderTypeChange(handleChange)}>
                  {
                    Object.values(TemplateType).map((templateType: TemplateType): JSX.Element => <MenuItem key={templateType} value={templateType}>{templateType}</MenuItem>)
                  }
                </Select>
                <Button id={'btn-advanced-filter'} className={`advance-filter-button ${openAdvancedSearch ? 'selected' : ''}`} onClick={handleAdvanceFilter}>
                  <FilterListIcon/>
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
      {
        trigger
          ? loading
            ? <LoadingComponent/>
            : titleTemplates.length > 0
                ? <Box>
                    <Typography id={'search-results-title'} className={'search-title'}>Showing results for: <span>{searchInput}</span></Typography>
                    <Paper className={'table-container'} component={Paper} elevation={0}>
                      <Box className={'toolbar-container'}>
                        {
                          selected.length > 1 &&
                            <Button
                              id={'btn-delete-titles'}
                              disabled={!deleteAllPermission()}
                              disableElevation
                              disableRipple
                              className={`toolbar-button center-end ${!deleteAllPermission() ? 'disable' : ''}`}
                              variant="contained"
                              startIcon={<DeleteIcon/>}
                              onClick={(): void => setOpenConfirmDialog(true)}>
                              Delete
                            </Button>
                        }
                        <Button
                          id={'btn-download-titles'}
                          disabled={submitLoading || selected.length < 2}
                          disableElevation
                          disableRipple
                          className={`toolbar-button ${selected.length > 1 ? 'end' : 'center-end'} ${submitLoading || selected.length < 2 ? 'disable' : ''}`}
                          variant='contained'
                          startIcon={submitLoading ? <CircularProgress size={14} color="inherit" /> : <FileDownloadOutlinedIcon />}
                          onClick={handleDownloadAll}>
                          {
                            submitLoading ? 'Downloading' : 'Download'
                          }
                        </Button>
                      </Box>
                      <TableContainer className={'zitles-table'}>
                        <Table>
                          <TableHead>
                            <TableRow className={'table-header'}>
                              <TableCell className={'no-wrap'} padding='checkbox'/>
                              <TableCell className={'no-wrap'} padding='checkbox'>
                                <Checkbox
                                  id={'checkbox-title-bank-table'}
                                  className={'table-checkbox header'}
                                  indeterminate={selected.length > 0 && selected.length < titleTemplates.length}
                                  checked={titleTemplates.length > 0 && selected.length === titleTemplates.length}
                                  onChange={handleSelectAll}/>
                              </TableCell>
                              {
                                tableHeaders.map((header: string): JSX.Element => (
                                  <TableCell key={header} id={`column-title-bank-${header}`} className={'no-wrap'} align='left' padding='normal'>
                                    <TableSortLabel
                                      className={'table-header-text'}
                                      active={true}
                                      direction={getSortDirection(header)}
                                      onClick={(): Promise<void> => handleSort(getTemplateColumnHeader(header))}>
                                      {header}
                                    </TableSortLabel>
                                  </TableCell>
                                ))
                              }
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {
                              titleTemplates.map(renderTableRow)
                            }
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                    {
                      titleTemplates.length > 0 &&
                        <TablePagination
                          currentPage={currentPage}
                          totalPages={totalPages}
                          onChange={handlePaginationChange}/>
                    }
                  </Box>
                : <Typography id={'empty-text-title-bank-table'} className='empty-message'>No title templates found !!</Typography>
          : null
      }
      {
        openAdvancedSearch &&
          <AdvancedSearchDialog
            open={openAdvancedSearch}
            setOpen={setOpenAdvancedSearch}
            handleSubmit={handleAdvanceSearch}/>
      }
      {
        openConfirmDialog &&
          <ConfirmDialog
            open={openConfirmDialog}
            setOpen={setOpenConfirmDialog}
            title={`Delete Template${selected.length > 1 ? 's' : ''}`}
            message={`Are you sure you want to delete ${selected.length > 1 ? selected.length + ' title templates?' : 'this title template?'}`}
            loading={loadingConfirmBtn}
            handleSubmit={deleteTitleTemplates}/>
      }
      {
        openTemplateDialog &&
          <TemplateDialog
            open={openTemplateDialog}
            setOpen={setOpenTemplateDialog}
            mode={mode}
            titleTemplate={selectedTemplate}
            reloadCallback={(): void => handleSearchTemplates(currentPage)}
            importTitle={importTitle}
            orderNumber={orderNumber}/>
      }
    </>
  );
}

export default observer(TitleBank);
